/**
 * Driver Vehicle Photos Upload Component
 * Allows drivers to upload exactly 2 photos: exterior and cargo area
 * Features:
 * - Client-side preview
 * - Upload progress
 * - Delete/replace functionality
 * - Status display (pending/approved/rejected)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Camera, Trash2, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ImageCropper from '../components/ImageCropper';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PHOTO_TYPES = {
  exterior: {
    label: 'Vehicle Exterior',
    description: 'Clear photo of the entire vehicle from the side',
    icon: '🚗',
    tips: ['Show the full vehicle', 'Good lighting', 'Clean background']
  },
  cargo_area: {
    label: 'Cargo Area',
    description: 'Photo of the cargo space/bed/trunk',
    icon: '📦',
    tips: ['Show cargo space open', 'Demonstrate capacity', 'Clean interior']
  }
};

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: AlertCircle, label: 'Pending Review' },
  approved: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: Check, label: 'Approved' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: X, label: 'Rejected' }
};

export default function DriverVehiclePhotos({ token: propToken }) {
  // Get token from prop or localStorage
  const token = propToken || localStorage.getItem('token');
  const [photos, setPhotos] = useState({ exterior: null, cargo_area: null });
  const [previews, setPreviews] = useState({ exterior: null, cargo_area: null });
  const [uploading, setUploading] = useState({ exterior: false, cargo_area: false });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCropper, setShowCropper] = useState(false);
  const [pendingPhotoType, setPendingPhotoType] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  // Handle cropped photo upload for vehicle photos
  const handleCroppedUpload = useCallback(async (croppedBlob) => {
    if (!pendingPhotoType) return;
    const type = pendingPhotoType;
    setShowCropper(false);
    setPendingPhotoType(null);
    setPendingFile(null);
    setUploading(prev => ({ ...prev, [type]: true }));
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', croppedBlob, `vehicle_${type}.jpg`);
      formData.append('photo_type', type);
      
      const response = await fetch(`${API_BASE}/api/driver/vehicle/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      setPhotos(prev => ({ ...prev, [type]: data.photo || data }));
      setPreviews(prev => ({ ...prev, [type]: null }));
      setSuccess(`${PHOTO_TYPES[type]?.label || type} photo uploaded successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to upload ${type} photo`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  }, [pendingPhotoType, token]);

  // Fetch existing photos on mount
  const fetchPhotos = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/driver/vehicle/photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const photoMap = { exterior: null, cargo_area: null };
        
        if (data.photos) {
          data.photos.forEach(photo => {
            if (photo.photo_type === 'exterior' || photo.photo_type === 'cargo_area') {
              photoMap[photo.photo_type] = photo;
            }
          });
        }
        setPhotos(photoMap);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
      setError('Failed to load existing photos');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Handle file selection with preview
  const handleFileSelect = (photoType, file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please use JPEG, PNG, or WebP.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    // Open cropper for the selected file
    setPendingPhotoType(photoType);
    setPendingFile(file);
    setShowCropper(true);
  };

  // Upload photo to server
  const uploadPhoto = async (photoType, file) => {
    setUploading(prev => ({ ...prev, [photoType]: true }));
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', photoType);

      const response = await fetch(`${API_BASE}/api/driver/vehicle/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      setSuccess(`${PHOTO_TYPES[photoType].label} uploaded successfully!`);
      
      // Refresh photos
      await fetchPhotos();
      
      // Clear preview since we now have the actual photo
      setPreviews(prev => ({ ...prev, [photoType]: null }));

    } catch (err) {
      setError(err.message);
      setPreviews(prev => ({ ...prev, [photoType]: null }));
    } finally {
      setUploading(prev => ({ ...prev, [photoType]: false }));
    }
  };

  // Delete photo
  const deletePhoto = async (photoType) => {
    if (!photos[photoType]) return;
    
    setUploading(prev => ({ ...prev, [photoType]: true }));
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/driver/vehicle/photos/${photos[photoType].id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Delete failed');
      }

      setPhotos(prev => ({ ...prev, [photoType]: null }));
      setSuccess(`${PHOTO_TYPES[photoType].label} deleted`);

    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(prev => ({ ...prev, [photoType]: false }));
    }
  };

  // Render photo card
  const renderPhotoCard = (photoType) => {
    const config = PHOTO_TYPES[photoType];
    const photo = photos[photoType];
    const preview = previews[photoType];
    const isUploading = uploading[photoType];
    const status = photo?.status || null;
    const statusConfig = status ? STATUS_STYLES[status] : null;

    return (
      <Card key={photoType} className="overflow-hidden" data-testid={`photo-card-${photoType}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{config.icon}</span>
              <div>
                <CardTitle className="text-lg">{config.label}</CardTitle>
                <CardDescription className="text-sm">{config.description}</CardDescription>
              </div>
            </div>
            {statusConfig && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.bg} ${statusConfig.text}`}>
                <statusConfig.icon className="w-3 h-3" />
                {statusConfig.label}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Photo display area */}
          <div className="relative aspect-video bg-slate-100 rounded-lg overflow-hidden mb-4">
            {(photo || preview) ? (
              <>
                <img 
                  src={preview || `${API_BASE}/api/driver/vehicle/photos/${photo?.id}/image`} 
                  alt={config.label}
                  className="w-full h-full object-cover"
                />
                {isUploading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                <Camera className="w-12 h-12 mb-2" />
                <span className="text-sm">No photo uploaded</span>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="mb-4 text-xs text-slate-500">
            <span className="font-medium">Tips:</span>
            <ul className="list-disc list-inside mt-1">
              {config.tips.map((tip, i) => <li key={i}>{tip}</li>)}
            </ul>
          </div>

          {/* Rejection reason */}
          {status === 'rejected' && photo?.rejection_reason && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg text-sm text-red-700">
              <strong>Reason:</strong> {photo.rejection_reason}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <label className="flex-1">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => e.target.files[0] && handleFileSelect(photoType, e.target.files[0])}
                disabled={isUploading}
                data-testid={`upload-input-${photoType}`}
              />
              <Button 
                variant={photo ? "outline" : "default"}
                className="w-full cursor-pointer"
                disabled={isUploading}
                asChild
              >
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {photo ? 'Replace Photo' : 'Upload Photo'}
                </span>
              </Button>
            </label>
            {photo && (
              <Button 
                variant="destructive" 
                size="icon"
                onClick={() => deletePhoto(photoType)}
                disabled={isUploading}
                data-testid={`delete-btn-${photoType}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 space-y-6" data-testid="driver-vehicle-photos">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-slate-900">Vehicle Photos</h2>
        <p className="text-slate-600 mt-1">
          Upload 2 photos of your vehicle for verification
        </p>
      </div>

      {/* Status messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 flex items-center gap-2">
          <Check className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Overall status */}
      {photos.exterior && photos.cargo_area && (
        <div className={`p-4 rounded-lg text-center ${
          photos.exterior.status === 'approved' && photos.cargo_area.status === 'approved'
            ? 'bg-emerald-100 text-emerald-800'
            : 'bg-amber-100 text-amber-800'
        }`}>
          {photos.exterior.status === 'approved' && photos.cargo_area.status === 'approved' ? (
            <span className="font-medium">✓ Your vehicle photos have been approved!</span>
          ) : (
            <span className="font-medium">⏳ Pending admin approval. You'll be notified once reviewed.</span>
          )}
        </div>
      )}

      {/* Photo cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {renderPhotoCard('exterior')}
        {renderPhotoCard('cargo_area')}
      </div>

      {/* Requirements */}
      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-slate-600 dark:text-slate-300">
        <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-2">Photo Requirements:</h4>
        <ul className="list-disc list-inside space-y-1">
          <li>File types: JPEG, PNG, or WebP</li>
          <li>Maximum size: 5MB per photo</li>
          <li>Both photos are required for verification</li>
          <li>Photos will be reviewed within 24-48 hours</li>
        </ul>
      </div>
      
      {/* Vehicle Photo Cropper - 4:3 aspect ratio */}
      <ImageCropper
        isOpen={showCropper}
        onClose={() => { setShowCropper(false); setPendingPhotoType(null); setPendingFile(null); }}
        onCropComplete={handleCroppedUpload}
        aspectRatio={4/3}
        imageType="vehicle"
        initialFile={pendingFile}
      />
    </div>
  );
}

// Page wrapper with layout
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';

export function DriverVehiclePhotosPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/driver/dashboard')} variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold">Liantiao</span>
              <Badge className="bg-green-500 text-white">DRIVER</Badge>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-6 py-8">
        <DriverVehiclePhotos />
      </main>
    </div>
  );
}
